<template>
  <div
    class="rounded-lg border-2 bg-white transition"
    :class="{
      'border-black': selected,
      'cursor-pointer': !selected,
      'border-shade-300': !selected || disabled,
      'text-shade-400': disabled,
    }"
    @click="(e: any) => $emit('click:select', e)"
  >
    <div class="flex items-center gap-x-2 px-6 py-4">
      <component
        :is="resolveComponent(icon)"
        v-if="icon"
        class="size-6 shrink-0"
      />
      <div class="flex grow items-center gap-2">
        <Headline tag="span" look="h4">{{ title }}</Headline>
        <slot name="afterTitle" />
      </div>
      <div
        v-if="showPrice"
        class="text-nowrap text-lg font-medium"
        data-testid="price"
      >
        {{ price ? `+ ${toCurrency(price)}` : $t('pdp.glasses.inclusive') }}
      </div>
      <div
        class="size-5 shrink-0 rounded-full ring-inset transition-shadow"
        :class="{
          'ring-[6px] ring-black': selected,
          'ring-2': !selected,
          'ring-shade-300': disabled || !selected,
        }"
      ></div>
    </div>
    <template v-if="$slots.direct">
      <slot name="direct" />
    </template>
    <div
      v-else
      class="overflow-hidden transition-height"
      :style="
        !collapsible
          ? { height: 'auto' }
          : collapsible && selected
            ? { height: collapsibleContainerHeight }
            : { height: 0 }
      "
    >
      <div ref="collapsibleContainer">
        <div
          v-if="description"
          v-dompurify-html="description"
          class="px-6 pb-4 text-base font-normal text-shade-600"
        />
        <slot />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
defineProps({
  title: {
    type: String,
    required: true,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  description: {
    type: String,
    default: '',
  },
  showPrice: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  price: {
    type: Number as PropType<number>,
    default: 0,
  },
  icon: {
    type: String as PropType<string>,
    default: '',
  },
  collapsible: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
})

defineEmits(['click:select'])

const collapsibleContainer = useTemplateRef('collapsibleContainer')
const { height } = useElementSize(
  collapsibleContainer,
  { width: 0, height: 0 },
  { box: 'border-box' },
)
const collapsibleContainerHeight = computed(() => `${height.value || 0}px`)
</script>
